import vue from 'vue'
import validate, { AsyncValidateOption, ValidateOption } from 'validate.js'

export default function Validation (_vue: typeof vue, options?: any): void {
  _vue.prototype.$validation = {
    ...validate,
    ...customValidators,
    vuetify: {
      validate (
        constraints: any,
        options?: ValidateOption
      ): (attributes: any) => true | string {
        return (attributes) => {
          const res: string[] | undefined = validate.validate(
            attributes,
            constraints,
            options
          )
          return res === undefined ? true : res.join(',')
        }
      },
      single (
        constraints: any,
        options?: ValidateOption
      ): (value: any) => true | string {
        return (value) => {
          const res: string[] | undefined = validate.single(
            value,
            constraints,
            options
          )
          return res === undefined ? true : res.join(',')
        }
      }
    }
  }
}

function mod97 (digital: number | string) {
  digital = digital.toString()
  let checksum: number | string = digital.slice(0, 2)
  let fragment = ''
  for (let offset = 2; offset < digital.length; offset += 7) {
    fragment = checksum + digital.substring(offset, offset + 7)
    checksum = parseInt(fragment, 10) % 97
  }
  return checksum
}

function validateUSTID (ustid: string): boolean {
  ustid = ustid.replace(/\s+/g, '').toUpperCase()
  const regex = /^DE[0-9]{9}$/
  if (!regex.test(ustid)) {
    return false
  }
  const digits = ustid.substring(2)
  const checkDigit = calculateCheckDigit(digits.substring(0, 8))
  return checkDigit === parseInt(digits.charAt(8), 10)
}

function calculateCheckDigit (digits: string): number {
  let product = 10
  for (let i = 0; i < digits.length; i++) {
    let sum = (parseInt(digits.charAt(i), 10) + product) % 10
    if (sum === 0) {
      sum = 10
    }
    product = (2 * sum) % 11
  }
  const checkDigit = (11 - product) % 10
  return checkDigit
}

export const customValidators: {
  isIban: (value: string) => true | string,
  isBic: (value: string) => true | string,
  isAddress: (value: string) => true | string,
  isTaxNumber: (value: string) => true | string,
  isSalesTaxId: (value: string) => true | string,
  isSalesTaxPercentage: (value: string) => true | string,
  isOperatorName: (value: string) => true | string,
  isPostalCode: (value: string) => true | string,
  isCity: (value: string) => true | string,
  isStationName: (value: string) => true | string,
  isLongitude: (value: string) => true | string,
  isLatitude: (value: string) => true | string,
  minLength (length: number): (value: string | null) => boolean | string
} = {
  // OPERATOR VALIDATIONS
  isIban (value: string | null): string | true {
    if (!value) return 'Darf nicht leer sein!'
    const codeLengths: { [id: string]: number } = {
      AD: 24,
      AE: 23,
      AL: 28,
      AT: 20,
      AZ: 28,
      BA: 20,
      BE: 16,
      BG: 22,
      BH: 22,
      BR: 29,
      CH: 21,
      CR: 21,
      CY: 28,
      CZ: 24,
      DE: 22,
      DK: 18,
      DO: 28,
      EE: 20,
      ES: 24,
      LC: 30,
      FI: 18,
      FO: 18,
      FR: 27,
      GB: 22,
      GI: 23,
      GL: 18,
      GR: 27,
      GT: 28,
      HR: 21,
      HU: 28,
      IE: 22,
      IL: 23,
      IS: 26,
      IT: 27,
      JO: 30,
      KW: 30,
      KZ: 20,
      LB: 28,
      LI: 21,
      LT: 20,
      LU: 20,
      LV: 21,
      MC: 27,
      MD: 24,
      ME: 22,
      MK: 19,
      MR: 27,
      MT: 31,
      MU: 30,
      NL: 18,
      NO: 15,
      PK: 24,
      PL: 28,
      PS: 29,
      PT: 25,
      QA: 29,
      RO: 24,
      RS: 22,
      SA: 24,
      SE: 24,
      SI: 19,
      SK: 24,
      SM: 27,
      TN: 24,
      TR: 26
    }
    const iban = value.toUpperCase().replace(/[^A-Z0-9]/g, '')
    const code = iban.match(/^([A-Z]{2})(\d{2})([A-Z\d]+)$/)
    if (!code || iban.length !== codeLengths[code[1]]) {
      return 'Keine valider Ländercode'
    }
    const digits = (code[3] + code[1] + code[2]).replace(
      /[A-Z]/g,
      (letter: string): string => {
        return (letter.charCodeAt(0) - 55).toString()
      }
    )
    return mod97(digits) === 1 ? true : 'Keine valide IBAN'
  },
  isBic (value: string | null) {
    if (!value) return 'Darf nicht leer sein!'

    return /^[a-zA-Z]{6}[2-9a-zA-Z][0-9a-nA-Np-zP-Z]([a-zA-Z0-9]{3}|x{3})?$/.test(
      value
    )
      ? true
      : 'Keine valide BIC'
  },
  isAddress (value: string | null) {
    if (!value) return 'Darf nicht leer sein!'
    return /^\S.+\S$/.test(value) ? true : 'Keine valide Adresse'
  },
  isTaxNumber  (value: string | null) {
    if (!value) return 'Darf nicht leer sein!'
    // no real checks in case company is outside of germany
    return checkFormat(value) ? true : 'Keine valide Steuernummer'
  },
  isSalesTaxId  (value: string | null) {
    if (!value) return 'Darf nicht leer sein!'
    // no real checks in case company is outside of germany
    return checkFormat(value) && validateUSTID(value) ? true : 'Keine valide Umsatzsteuer-ID'
  },
  isSalesTaxPercentage  (value: number | string) {
    // No check for full integer since actual value could be something like 17.5%
    if (Number(value) < 0) return 'Wert darf nicht kleiner als 0% sein' // why not? Negative Sales Tax could be some form of subsidy
    return Number(value) > 100 ? 'Wert darf nicht größer als 100% sein' : true
  },
  isOperatorName (value: string | null) {
    if (!value) return 'Darf nicht leer sein!'
    if (value.length < 3) return 'Muss mindestens 3 Zeichen lang sein'
    return checkFormat(value) ? true : 'Keine valider Name'
  },
  isPostalCode (value: string | null) {
    if (!value) return 'Darf nicht leer sein!'
    // This is a very weak check in case there are postal codes from outside of germany
    return checkFormat(value) ? true : 'Keine valide Postleitzahl'
  },
  isCity (value: string | null) {
    if (!value) return 'Darf nicht leer sein!'
    return checkFormat(value) ? true : 'Kein valider Stadtname'
  },
  // OPERATOR VALIDATIONS
  isStationName (value: string | null) {
    if (!value) return 'Darf nicht leer sein!'
    if (value.length < 3) return 'Muss mindestens 3 Zeichen lang sein'
    return checkFormat(value) ? true : 'Keine valider Name'
  },
  isLongitude (value: number | string | null) {
    if (!value) return 'Darf nicht leer sein!'
    if (value < -90) return 'Muss mindestens -90 sein'
    if (value > 90) return 'Darf höchstens 90 sein'
    return true
  },
  isLatitude (value: number | string | null) {
    if (!value) return 'Darf nicht leer sein!'
    if (value < -180) return 'Muss mindestens -180 sein'
    if (value > 180) return 'Darf höchstens 180 sein'
    return true
  },
  minLength (length: number): (value: string | null) => boolean | string {
    return (value: string | null) => {
      if (!value) return 'Darf nicht leer sein!'
      return value.length >= length ? true : `Muss mindestens ${length} Zeichen lang sein`
    }
  }
}

function checkFormat (value: string | null) {
  if (!value) return false
  // at least one charracter
  if (value.length < 1) return false
  // no leading or trailing SPACE
  if (value.charAt(0) === ' ' || value.charAt(value.length - 1) === ' ') return false
  // no TAB or NEWLINE at all
  if (value.includes('\t') || value.includes('\n') || value.includes('\r')) return false
  // no multiple SPACEs in a row
  if (value.includes('  ')) return false
  return true
}
